
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AuthUser, UserProfile, UserRole } from '@/types/user';

interface AuthContextProps {
  session: Session | null;
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, displayName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isAdmin: boolean;
  hasRole: (role: UserRole) => boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const fetchUserProfile = async (userId: string) => {
    try {
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;

      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (rolesError) throw rolesError;

      return {
        profile: profile as UserProfile,
        roles: roles.map(r => r.role) as UserRole[]
      };
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return { profile: null, roles: [] };
    }
  };

  const refreshUser = async () => {
    if (!user?.id) return;
    
    const { profile, roles } = await fetchUserProfile(user.id);
    
    if (profile) {
      setUser({
        ...user,
        profile,
        roles
      });
      setIsAdmin(roles.includes('admin'));
    }
  };

  useEffect(() => {
    const setupAuth = async () => {
      try {
        setLoading(true);

        // Set up auth state listener first
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, currentSession) => {
            console.log("Auth state changed:", event);
            setSession(currentSession);
            
            if (currentSession?.user) {
              console.log("User authenticated:", currentSession.user.email);
              const { profile, roles } = await fetchUserProfile(currentSession.user.id);
              
              setUser({
                ...currentSession.user,
                profile,
                roles
              });
              
              setIsAdmin(roles.includes('admin'));
            } else {
              console.log("User signed out or session expired");
              setUser(null);
              setIsAdmin(false);
            }
          }
        );

        // Check for existing session
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        console.log("Initial session check:", currentSession ? "Session found" : "No session");
        setSession(currentSession);

        if (currentSession?.user) {
          console.log("User from existing session:", currentSession.user.email);
          const { profile, roles } = await fetchUserProfile(currentSession.user.id);
          
          setUser({
            ...currentSession.user,
            profile,
            roles
          });
          
          setIsAdmin(roles.includes('admin'));
        }

        // Check for hash in URL to handle auth redirects on initial load
        if (window.location.hash && window.location.hash.includes("access_token")) {
          console.log("Found access_token in URL hash on initial load, processing auth...");
          try {
            // Let Supabase handle the auth redirect automatically
            const { data, error } = await supabase.auth.getSession();
            console.log("Auth redirect session result:", data.session ? "Session found" : "No session", error ? `Error: ${error.message}` : "No error");
          } catch (err) {
            console.error("Error processing auth redirect in AuthContext:", err);
          }
        }

        return () => subscription.unsubscribe();
      } catch (error) {
        console.error("Auth setup error:", error);
      } finally {
        setLoading(false);
      }
    };

    setupAuth();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signUp = async (email: string, password: string, displayName: string) => {
    try {
      const { error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          data: {
            display_name: displayName
          }
        }
      });
      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const hasRole = (role: UserRole): boolean => {
    return user?.roles?.includes(role) || false;
  };

  const value = {
    session,
    user,
    loading,
    signIn,
    signUp,
    signOut,
    refreshUser,
    isAdmin,
    hasRole
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
